import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

/**
 * Restyles react-querybuilder (the User Attributes / URL Parameters rule builders and the
 * random-failure condition builder) to sit in the panel like any other field group: an inset
 * well instead of the library's blue box, and quiet buttons sized like the MUI controls beside
 * them. Scoped to the panel so other query builders in the app keep their own look.
 *
 * Only the library's plain <button>s (add rule / add group) and the native operator <select> are
 * restyled here; the MUI controls the builders render through controlElements, including the
 * shared RemoveAction, already follow the theme.
 */
export const ruleBuilderSx = (theme: Theme): SystemStyleObject<Theme> => {
  const palette = (theme.vars ?? theme).palette;
  const plainButton = '& button:not(.MuiButtonBase-root)';

  return {
    '& .queryBuilder, & .zuul-query-builder': {
      '& .ruleGroup': {
        gap: 1,
        p: 1.5,
        bgcolor: 'zuul.inset',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
      },
      // A nested group reads as a card inside the well.
      '& .ruleGroup .ruleGroup': { bgcolor: 'background.paper' },
      '& .ruleGroup-header': { gap: 1, flexWrap: 'wrap' },
      '& .ruleGroup-body': { gap: 1 },
      '& .rule': { gap: 1, flexWrap: 'wrap', alignItems: 'center' },
      // Controls keep their own width; the field and value pickers (rendered 100% wide) share
      // what is left of the row, and the row wraps only once they would get too narrow.
      '& .rule > *': { flex: '0 0 auto' },
      '& .rule > .MuiAutocomplete-root, & .rule > .MuiTextField-root, & .rule > .MuiFormControl-root, & .rule > .rule-fields, & .rule > .rule-value':
        { flex: '1 1 10rem', minWidth: 0, width: 'auto' },
      '& .queryBuilder-dragHandle': { color: palette.text.secondary },
      '& select.rule-operators': {
        height: 40,
        minWidth: '5.5rem',
        px: 1,
        font: 'inherit',
        fontSize: '0.875rem',
        color: palette.text.primary,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
      },
      [plainButton]: {
        height: 30,
        minWidth: 30,
        px: 1.25,
        font: 'inherit',
        fontSize: '0.8125rem',
        fontWeight: 500,
        lineHeight: 1,
        color: palette.text.primary,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        cursor: 'pointer',
        '&:hover:not(:disabled)': { borderColor: palette.primary.main, color: palette.primary.main },
        '&:disabled': { opacity: 0.5, cursor: 'default' },
      },
    },
  };
};
